import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { T, paths } from "../i18n";
import { SERVICES } from "../data/services";

export default function ServicesIndex({ lang }) {
  const t = T[lang];
  const p = paths(lang);
  const [q, setQ] = useState("");
  const filtered = SERVICES.filter((s) =>
    (s[lang].name + " " + s[lang].short + " " + s[lang].keywords.join(" ")).toLowerCase().includes(q.toLowerCase())
  );

  const title = lang === "fr" ? "Services de sécurité au Québec — 14 programmes | 1st Nations Security" : "Security Services in Quebec — 14 Programs | 1st Nations Security";
  const desc = lang === "fr"
    ? "Explorez nos 14 services de sécurité au Québec : gardiens de sécurité, patrouille mobile, sécurité événementielle, gardiennage de chantier, surveillance incendie, prévention des pertes et plus. Compagnie 100 % autochtone, répartition 24/7."
    : "Explore our 14 security services in Quebec: security guards, mobile patrol, event security, construction site security, fire watch, loss prevention and more. 100% Indigenous-owned, 24/7 dispatch.";

  return (
    <div data-testid="services-index-page">
      <Seo lang={lang} title={title} description={desc} enPath="/security-services" frPath="/fr/services-de-securite"
        jsonLd={breadcrumbJsonLd([[t.breadcrumbHome, p.home], [t.breadcrumbServices, p.services]])} />
      <PageHeader lang={lang} eyebrow={t.brandTag} title={t.servicesTitle}
        breadcrumbs={[[t.breadcrumbHome, p.home], [t.breadcrumbServices, null]]} />

      <div className="mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
        <div className="relative max-w-xl">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.searchService}
            className="w-full rounded-full border border-slate-700 bg-card/80 py-3 pl-11 pr-4 text-sm text-slate-200 placeholder:text-slate-500 focus:border-amber-500/60 focus:outline-none"
            data-testid="service-search-input"
          />
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.slug} delay={(i % 3) * 0.07}>
                <Link to={p.service(s.slug)} className="group flex h-full flex-col rounded-2xl border border-slate-800/70 bg-card/60 p-6 transition-all hover:-translate-y-1 hover:border-amber-500/40" data-testid={`services-index-card-${s.slug}`}>
                  <Icon size={28} className="text-amber-400" />
                  <h2 className="mt-4 font-display text-xl font-bold text-slate-100 group-hover:text-amber-300">{s[lang].name}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{s[lang].short}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {s[lang].features.slice(0, 2).map((f) => (
                      <span key={f} className="rounded-full bg-elevated px-2.5 py-1 text-[11px] text-slate-400">{f}</span>
                    ))}
                  </div>
                  <span className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-amber-400">{t.viewService} <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" /></span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>
      <CtaBand lang={lang} />
    </div>
  );
}
